/**
 * O push na casca (#536, ADR-0017): manter a inscrição em dia e abrir a
 * conversa quando a pessoa clica no aviso.
 *
 * ── O clique chega por dois caminhos ─────────────────────────────────────
 *
 *   - com uma aba do CRM aberta, o service worker a traz para frente e manda
 *     `cliqcrm:abrir-conversa` — a aba abre a conversa sem recarregar;
 *   - sem aba, ele abre o CRM com `?conversa=…&revenda=…`, e a casca lê isso
 *     ao montar.
 *
 * A revenda vai junto porque quem pertence a mais de uma pode estar logado em
 * outra: abrir a conversa de uma revenda na sessão de outra daria 404. Aí a
 * conversa não abre, e o card da revenda certa continua esperando.
 */

import { useEffect, useRef } from 'react';
import type { ClienteDaApi } from '@cliqcrm/contracts';
import { sincronizarPush, suportaPush } from '../lib/push';

export function useSincronizarPush(api: ClienteDaApi | null): void {
  const feito = useRef(false);
  useEffect(() => {
    if (!api || feito.current) return;
    feito.current = true;
    // Manutenção silenciosa: sem permissão, sem inscrição ou sem servidor, não faz nada.
    void sincronizarPush(api).catch(() => undefined);
  }, [api]);
}

export function useAberturaPeloAviso(
  revendaAtual: string | null,
  abrirConversa: (id: string) => void,
): void {
  const abrir = useRef(abrirConversa);
  abrir.current = abrirConversa;

  // Sem aba aberta: o service worker abriu o CRM com a conversa na URL.
  useEffect(() => {
    if (!revendaAtual) return;
    const parametros = new URLSearchParams(window.location.search);
    const conversa = parametros.get('conversa');
    if (!conversa) return;
    const revenda = parametros.get('revenda');
    // Limpa a URL: recarregar não pode reabrir a conversa de um aviso antigo.
    parametros.delete('conversa');
    parametros.delete('revenda');
    const busca = parametros.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${busca ? `?${busca}` : ''}`);
    if (!revenda || revenda === revendaAtual) abrir.current(conversa);
  }, [revendaAtual]);

  // Com aba aberta: o service worker a trouxe para frente e avisou.
  useEffect(() => {
    if (!suportaPush()) return;
    const aoReceber = (evento: MessageEvent): void => {
      const dados = evento.data as { tipo?: string; conversa?: string; revenda?: string | null };
      if (dados?.tipo !== 'cliqcrm:abrir-conversa' || !dados.conversa) return;
      if (dados.revenda && revendaAtual && dados.revenda !== revendaAtual) return;
      abrir.current(dados.conversa);
    };
    navigator.serviceWorker.addEventListener('message', aoReceber);
    return () => navigator.serviceWorker.removeEventListener('message', aoReceber);
  }, [revendaAtual]);
}
